import { Edit } from 'lucide-react';
import { ReactNode, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  CreateFirmPayload,
  Firm,
  UpdateFirmPayload,
} from '@/services/types/firms-types';

import { EditFirm } from './EditFirm';
import { ViewFirm } from './ViewFirm';

export interface FirmContainerProps {
  firm?: Firm;
  onSubmit?: (values: CreateFirmPayload | UpdateFirmPayload) => Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  inline?: boolean;
  initialMode?: 'view' | 'edit' | 'create';
  header?: ReactNode;
}

export const FirmContainer: React.FC<FirmContainerProps> = ({
  firm,
  onSubmit,
  onCancel,
  isSubmitting = false,
  inline = false,
  initialMode = firm ? 'view' : 'create',
  header,
}) => {
  const [mode, setMode] = useState<'view' | 'edit' | 'create'>(initialMode);

  const handleEditClick = () => {
    setMode('edit');
  };

  const handleCancelClick = () => {
    // If external onCancel provided (e.g., for navigation), use it
    // Otherwise, if we have a firm, go back to view mode
    if (onCancel) {
      onCancel();
    } else if (firm) {
      setMode('view');
    }
  };

  const handleSubmitSuccess = async (
    values: CreateFirmPayload | UpdateFirmPayload,
  ) => {
    if (onSubmit) {
      await onSubmit(values);
    }

    // After successful submit, switch to view mode if we were editing
    if (mode === 'edit') {
      setMode('view');
    }
  };

  // View mode header with edit button
  const viewHeader = firm ? (
    <div className="flex justify-between items-center border-b p-4">
      <h2 className="text-lg font-semibold">{header || 'Firm Details'}</h2>
      <Button
        variant="outline"
        size="sm"
        onClick={handleEditClick}
        className="gap-1"
      >
        <Edit className="h-4 w-4" />
        Edit
      </Button>
    </div>
  ) : (
    <div className="border-b p-4">
      <h2 className="text-lg font-semibold">{header || 'Create Firm'}</h2>
    </div>
  );

  // Show appropriate component based on mode
  if (mode === 'view' && firm) {
    return <ViewFirm firm={firm} header={viewHeader} inline={inline} />;
  }

  return (
    <EditFirm
      initialValues={firm}
      onSubmit={handleSubmitSuccess}
      onCancel={handleCancelClick}
      isSubmitting={isSubmitting}
      mode={firm && mode === 'edit' ? 'edit' : 'create'}
      inline={inline}
      firm={firm}
      header={mode === 'view' ? viewHeader : header}
    />
  );
};
